import React, { useState, useEffect, useCallback } from 'react';
import { FormulaReviewItem } from '../types';
import { fetchFormulaReviewQueue, updateFormulaReviewQueue } from '../lib/api';
import { FORMULA_EQUATIONS } from '../data/formulas';
import { ShieldAlert, CheckCircle2, XCircle, Clock, BookOpen, AlertTriangle } from 'lucide-react';

export const FormulaReviewQueueView: React.FC = () => {
  const [items, setItems] = useState<FormulaReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedResolutions, setSelectedResolutions] = useState<Record<string, string>>({});
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFormulaReviewQueue();
      setItems(data);
    } catch {
      // fallback
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const handleResolve = async (id: string) => {
    const assignedFormulaId = selectedResolutions[id];
    if (!assignedFormulaId) {
      alert('Selecione uma fórmula canônica do prontuário para associar.');
      return;
    }

    try {
      await updateFormulaReviewQueue(id, {
        status: 'RESOLVED',
        assignedFormulaId,
      });
      setFeedbackMessage(`Questão resolvida com sucesso e associada à fórmula "${assignedFormulaId}"!`);
      loadQueue();
    } catch {
      alert('Erro ao resolver item da fila.');
    }
  };

  const handleReject = async (id: string) => {
    try {
      await updateFormulaReviewQueue(id, {
        status: 'REJECTED',
      });
      setFeedbackMessage('Item marcado como rejeitado / sem fórmula correspondente.');
      loadQueue();
    } catch {
      alert('Erro ao rejeitar item.');
    }
  };

  const pendingItems = items.filter((i) => i.status === 'PENDING');
  const resolvedItems = items.filter((i) => i.status !== 'PENDING');

  return (
    <div className="h-full flex flex-col overflow-hidden p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-paper-300 pb-4 dark:border-study-line">
        <div>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <h2 className="font-serif text-lg font-bold text-zinc-900 dark:text-zinc-100">
              Fila de Revisão & Curadoria de Fórmulas
            </h2>
          </div>
          <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
            Questões extraídas de cadernos de prova cuja fórmula bruta não pôde ser resolvida com confiança matemática estrita. Nenhuma questão é associada aleatoriamente.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="rounded-full bg-amber-100 px-3 py-1 font-mono text-xs font-bold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
            {pendingItems.length} pendente{pendingItems.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {feedbackMessage && (
        <div className="flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span>{feedbackMessage}</span>
        </div>
      )}

      {/* Queue List */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-1">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-xs text-zinc-500">
            <Clock className="h-4 w-4 animate-spin mr-2" />
            Carregando fila de curadoria...
          </div>
        ) : pendingItems.length === 0 ? (
          <div className="rounded-xl border border-dashed border-paper-300 bg-paper-100 p-8 text-center dark:border-study-line dark:bg-study-panel">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500 mb-2" />
            <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
              Nenhuma questão pendente de revisão
            </p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              Todas as fórmulas dos cadernos ingeridos foram resolvidas deterministicamente ou revisadas por um autor.
            </p>
          </div>
        ) : (
          pendingItems.map((item) => (
            <div
              key={item.id}
              className="rounded-xl border border-amber-300 bg-paper-50 p-5 shadow-sm dark:border-amber-800/70 dark:bg-study-card space-y-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-paper-200 pb-2.5 dark:border-study-line">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                    {item.caderno} • Questão {item.questionNumber}
                  </span>
                  <span className="text-xs font-medium text-zinc-500">
                    {item.subject}
                  </span>
                </div>
                <span className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400 font-mono">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {item.reason}
                </span>
              </div>

              {/* Statement Preview */}
              <div className="text-xs text-zinc-700 dark:text-zinc-300 leading-relaxed bg-paper-100 dark:bg-study-panel p-3 rounded">
                <p className="font-semibold text-[11px] uppercase tracking-wider text-zinc-400 mb-1">
                  Enunciado Extraído:
                </p>
                <p className="line-clamp-3">{item.statement}</p>
              </div>

              {/* Raw formula vs Assignment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block mb-1">
                    Fórmula Bruta Identificada:
                  </span>
                  <div className="font-mono text-xs bg-paper-200 dark:bg-study-slate p-2 rounded text-zinc-800 dark:text-zinc-200 truncate">
                    {item.extractedRawFormula || '(Nenhuma fórmula explícita detectada)'}
                  </div>
                </div>

                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block mb-1">
                    Associar à Fórmula Canônica do Prontuário:
                  </span>
                  <select
                    value={selectedResolutions[item.id] || ''}
                    onChange={(e) =>
                      setSelectedResolutions((prev) => ({ ...prev, [item.id]: e.target.value }))
                    }
                    className="w-full rounded border border-paper-300 bg-paper-50 p-2 text-xs font-mono text-zinc-900 focus:border-indigo-500 focus:outline-none dark:border-study-line dark:bg-study-panel dark:text-zinc-100"
                  >
                    <option value="">Selecione a fórmula canônica...</option>
                    {FORMULA_EQUATIONS.map((eq) => (
                      <option key={eq.id} value={eq.id}>
                        {eq.name} ({eq.latex})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-paper-200 dark:border-study-line">
                <button
                  type="button"
                  onClick={() => handleReject(item.id)}
                  className="flex items-center gap-1 rounded px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:bg-paper-200 dark:text-zinc-400 dark:hover:bg-study-line"
                >
                  <XCircle className="h-3.5 w-3.5 text-red-500" />
                  Descartar / Não é Fórmula
                </button>
                <button
                  type="button"
                  onClick={() => handleResolve(item.id)}
                  disabled={!selectedResolutions[item.id]}
                  className="flex items-center gap-1.5 rounded bg-zinc-900 px-4 py-1.5 text-xs font-bold text-white hover:bg-zinc-800 disabled:opacity-40 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  Homologar Associação
                </button>
              </div>
            </div>
          ))
        )}

        {/* Resolved History Accordion */}
        {resolvedItems.length > 0 && (
          <div className="pt-6 border-t border-paper-200 dark:border-study-line">
            <span className="text-xs font-bold text-zinc-500 dark:text-zinc-400 block mb-3">
              Itens Homologados / Concluídos ({resolvedItems.length})
            </span>
            <div className="space-y-2">
              {resolvedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded border border-paper-200 bg-paper-100 p-3 text-xs dark:border-study-line dark:bg-study-panel"
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-3.5 w-3.5 text-zinc-400" />
                    <span className="font-semibold text-zinc-800 dark:text-zinc-200">
                      {item.caderno} - Q{item.questionNumber}
                    </span>
                    <span className="text-[10px] text-zinc-500">
                      Fórmula associada: <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400">{item.assignedFormulaId || 'N/A'}</span>
                    </span>
                  </div>
                  <span className="rounded px-2 py-0.5 text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
